import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/core/api/supabase';
import { useConnectionStore } from '../stores/connection-store';
import { verifyConnection } from '@/core/api/supabase';

/**
 * Enhanced hook to maintain a persistent connection to Supabase
 * 
 * This hook:
 * 1. Sends a periodic ping to keep the TCP connection alive
 * 2. Actively monitors connection state and triggers refresh actions
 * 3. Automatically attempts reconnection when problems are detected
 * 4. Maintains global connection state for UI feedback
 * 
 * @param pingIntervalMs - Interval between pings (default: 3 minutes)
 * @param monitorIntervalMs - Interval between connection checks (default: 60 seconds) 
 * @returns void
 */
export const useConnectionKeepAlive = ({
  pingIntervalMs = 3 * 60 * 1000, // 3 minutes
  monitorIntervalMs = 60 * 1000,  // 60 seconds
} = {}) => {
  const { online, setState } = useConnectionStore();
  const reconnectInProgress = useRef(false);
  const failedPings = useRef(0);
  
  // Need to declare checkConnectionState ref before using it in pingConnection
  const checkConnectionStateRef = useRef<() => Promise<void>>();
  
  // Handle ping operations - keeps the WebSocket alive
  const pingConnection = useCallback(async () => {
    try {
      // Lightweight query instead of RPC to avoid missing function errors
      const { error } = await supabase.from('group_sessions').select('id').limit(1);
      
      if (!error) {
        if (process.env.NODE_ENV === 'development') {

        }
        failedPings.current = 0; // reset on success
      } else {

        failedPings.current += 1;
        if (failedPings.current >= 2 && checkConnectionStateRef.current) {
          // Only verify connection after two consecutive failures
          checkConnectionStateRef.current();
          failedPings.current = 0;
        }
      }
    } catch (err) {
      // Silently catch errors to avoid UI crashes
      if (process.env.NODE_ENV === 'development') {

      }
    }
  }, []);
  
  // Track reconnection attempts for exponential backoff
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  
  // Implement exponential backoff for reconnection
  const getBackoffDelay = useCallback(() => {
    // Start with 1 second, then 2, 4, 8, etc. up to 30 seconds max
    const baseDelay = 1000;
    const factor = 2;
    const maxDelay = 30000; // 30 seconds max
    
    const delay = Math.min(baseDelay * Math.pow(factor, reconnectAttempts.current), maxDelay);
    // Add some jitter to prevent all clients reconnecting at the same time
    const jitter = Math.random() * 0.3 * delay;
    return Math.floor(delay + jitter);
  }, []);
  
  // Actively check connection state with improved reconnection logic
  const checkConnectionState = useCallback(async () => {
    if (reconnectInProgress.current) {
      return; // Don't run multiple reconnects in parallel
    }
    
    const isConnected = await verifyConnection();
    setState({ online: isConnected, lastCheck: Date.now() });
    
    // If we're disconnected, try to reconnect
    if (!isConnected && !reconnectInProgress.current && online) {  // Only attempt reconnect if we previously thought we were online
      reconnectInProgress.current = true;
      
      try {

        // First try to get the current session
        const { data: sessionData } = await supabase.auth.getSession();
        
        // If we have a session, try to refresh it
        if (sessionData?.session) {

          const { error } = await supabase.auth.refreshSession();
          if (error) {

          } else {

          }
        } else {

        }
        
        // Verify the connection again after token refresh
        const reconnected = await verifyConnection();
        setState({ online: reconnected, lastCheck: Date.now() });
        
        if (reconnected) {

          reconnectAttempts.current = 0; // Reset attempts on success
          
          // Force refresh page data if needed
          if (typeof window !== 'undefined') {
            // Emit a custom event for components to react to
            const reconnectEvent = new CustomEvent('supabase:reconnected');
            window.dispatchEvent(reconnectEvent);
          }
        } else {
          // Increment attempt counter for backoff
          reconnectAttempts.current += 1;
          
          if (reconnectAttempts.current < maxReconnectAttempts) {
            // Schedule another attempt with backoff
            const delay = getBackoffDelay();

            // Schedule next attempt with backoff
            setTimeout(() => {
              reconnectInProgress.current = false;
              checkConnectionState();
            }, delay);
          } else {

            reconnectAttempts.current = 0; // Reset for future attempts
          }
        }
      } catch (err) {

        reconnectAttempts.current += 1;
      } finally {
        // Only set to false if we're not scheduling another attempt
        if (reconnectAttempts.current >= maxReconnectAttempts) {
          reconnectInProgress.current = false;
        }
      }
    }
  }, [setState, online, getBackoffDelay]);
  
  useEffect(() => {
    // Save reference to current checkConnectionState for use in pingConnection
    checkConnectionStateRef.current = checkConnectionState;
    
    // Register event listeners for online/offline
    const handleOnline = () => {

      checkConnectionState();
    };
    
    const handleOffline = () => {

      setState({ online: false, lastCheck: Date.now() });
    };
    
    // Handle tab visibility changes with token refresh
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {

        // Check if token is close to expiry (within 5 minutes)
        const { data } = await supabase.auth.getSession();
        if (data?.session) {
          const expiresAt = data.session.expires_at;
          if (expiresAt) {
            const expiryTime = new Date(expiresAt * 1000);
            const now = new Date();
            const timeUntilExpiry = expiryTime.getTime() - now.getTime();
            
            // If token expires in less than 5 minutes, refresh it
            if (timeUntilExpiry < 5 * 60 * 1000) {

              await supabase.auth.refreshSession();
            }
          }
        }
        
        // Force connection check when tab becomes visible
        checkConnectionState();
        
        // Also do an immediate ping to verify connection
        pingConnection();
      } else if (document.visibilityState === 'hidden') {

        // No action needed on hidden - just logging
      }
    };
    
    // Ping interval - keeps connection alive
    const pingId = setInterval(pingConnection, pingIntervalMs);
    
    // Monitor interval - tracks connection state
    const monitorId = setInterval(checkConnectionState, monitorIntervalMs);
    
    // Run initial checks
    checkConnectionState();
    pingConnection();
    
    // Register browser events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Custom event listener for components to request reconnection
    const handleReconnectRequest = () => {

      checkConnectionState();
    };
    window.addEventListener('supabase:reconnect-request', handleReconnectRequest);
    
    return () => {
      clearInterval(pingId);
      clearInterval(monitorId);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('supabase:reconnect-request', handleReconnectRequest);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pingConnection, checkConnectionState, setState, pingIntervalMs, monitorIntervalMs]);
};
